/**
 * useStoreDeliveries — Presentation layer hook (Clean Architecture).
 * Connects components to store deliveries use cases; no direct DataSource access.
 */

import {useState, useEffect, useCallback} from 'react';
import {storeFactory} from '../store/storeFactory';
import {StoreDeliveryTransaction, StoreDeliveryFilters} from '../models/storeDeliveries.model';

export interface UseStoreDeliveriesParams {
  storeId: string | null;
}

export interface UseStoreDeliveriesResult {
  transactions: StoreDeliveryTransaction[];
  transactionsLoading: boolean;
  transactionsError: string | null;
  pagination: { page: number; limit: number; total: number; total_pages: number };
  filters: StoreDeliveryFilters;
  setFilters: (f: StoreDeliveryFilters) => void;
  onPageChange: (page: number) => void;
  selectedTransactionId: string | null;
  selectedTransaction: StoreDeliveryTransaction | null;
  transactionLoading: boolean;
  transactionError: string | null;
  viewTransaction: (transactionId: string) => void;
  clearTransactionDetails: () => void;
}

export function useStoreDeliveries({storeId}: UseStoreDeliveriesParams): UseStoreDeliveriesResult {
  const [transactions, setTransactions] = useState<StoreDeliveryTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1
  });
  const [filters, setFiltersState] = useState<StoreDeliveryFilters>({page: 1, limit: 20});
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<StoreDeliveryTransaction | null>(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);

  const useCases = storeFactory.getUseCases();

  const loadTimeline = useCallback(async () => {
    if (!storeId) return;
    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
      const result = await useCases.getStoreDeliveriesTimeline.execute(storeId, filters);
      setTransactions(result.transactions);
      setPagination(result.pagination);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'حدث خطأ في جلب سجل التسليمات';
      setTransactionsError(message);
    } finally {
      setTransactionsLoading(false);
    }
  }, [storeId, filters, useCases.getStoreDeliveriesTimeline]);

  useEffect(() => {
    if (storeId) loadTimeline();
  }, [storeId, loadTimeline]);

  const setFilters = useCallback((newFilters: StoreDeliveryFilters) => {
    setFiltersState((prev) => ({...prev, ...newFilters}));
  }, []);

  const onPageChange = useCallback((page: number) => {
    setFiltersState((prev) => ({...prev, page}));
  }, []);

  const viewTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setTransactionLoading(true);
    setTransactionError(null);
    setSelectedTransaction(null);
    useCases.getStoreDeliveryTransaction
      .execute(transactionId)
      .then(setSelectedTransaction)
      .catch((err: unknown) => {
        setTransactionError(err instanceof Error ? err.message : 'حدث خطأ في جلب تفاصيل المعاملة');
      })
      .finally(() => setTransactionLoading(false));
  }, [useCases.getStoreDeliveryTransaction]);

  const clearTransactionDetails = useCallback(() => {
    setSelectedTransactionId(null);
    setSelectedTransaction(null);
    setTransactionError(null);
  }, []);

  return {
    transactions,
    transactionsLoading,
    transactionsError,
    pagination,
    filters,
    setFilters,
    onPageChange,
    selectedTransactionId,
    selectedTransaction,
    transactionLoading,
    transactionError,
    viewTransaction,
    clearTransactionDetails
  };
}
